import { Trash2 } from "lucide-react";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";

import FormInput from "@/components/form-fields/form-input";
import { FormSelectSingle } from "@/components/reusables/custom-form-fields/FormSelect";
import { useTheme } from "@/components/theme-provider";
import { Button } from "@/components/ui/button";
import * as Table from "@/components/ui/table";
import { ILoadType } from "@/interfaces/loadtype.interface";
import { IWasteType } from "@/interfaces/wastetype.interface";
import { cn } from "@/lib/utils";
import {
  useGetLoadTypeQuery,
  useGetWasteTypeQuery,
} from "@/redux/api/wasteManagementTypeQuery";
import { TDailyReport } from "@/schemas/daily-report.schema";
import { TWeightScaleBreakdown } from "@/schemas/weigh-scale-ticket.schema";

const ResourceRecoveryTableHeader = (): JSX.Element => {
  const { theme: skin } = useTheme();

  return (
    <Table.TableHeader>
      <Table.TableRow
        className={cn(skin !== "dark" && " bg-[#E2EBF5] hover:bg-secondary ")}
      >
        <Table.TableHead>SN</Table.TableHead>
        <Table.TableHead>LOAD TYPE</Table.TableHead>
        <Table.TableHead className='w-28'>LOAD QTY</Table.TableHead>
        <Table.TableHead>WASTE TYPE</Table.TableHead>
        <Table.TableHead className='w-40'>TOTAL WEIGHT</Table.TableHead>
        <Table.TableHead>BREAKDOWN</Table.TableHead>
        <Table.TableHead>TICKET</Table.TableHead>
        <Table.TableHead></Table.TableHead>
      </Table.TableRow>
    </Table.TableHeader>
  );
};

export default ResourceRecoveryTableHeader;

type RecoveryField = Record<"idx", string> & {
  id?: string;
  ticketNumber?: number;
  tippingId?: string;
};

interface IResourceRecoveryDetailsTableRow {
  setBreakDownIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedBreakdownId: React.Dispatch<React.SetStateAction<number>>;
  index: number;
  handleItemsDelete: (
    index: number,
    id: string | undefined,
    weighScaleTicketId?: string,
  ) => void;
  field: RecoveryField;
}

export const ResourceRecoveryDetailsTableRow = ({
  field,
  handleItemsDelete,
  index,
  setBreakDownIsOpen,
  setSelectedBreakdownId,
}: IResourceRecoveryDetailsTableRow): JSX.Element => {
  const form = useFormContext<TDailyReport>();
  const { watch, getValues, setValue } = form;
  const { data: loadTypeDataResponse, isLoading: loadtypeLoading } =
    useGetLoadTypeQuery({});
  const loadtypeData = loadTypeDataResponse?.data;
  const { data: wastetypeDataResponse, isLoading: wastetypeLoading } =
    useGetWasteTypeQuery({});
  const wastetypeData = wastetypeDataResponse?.data;
  const commonCellClassName = "bg-background hover:bg-background";
  // Check if the field is set to manual; disable the field if it is not manual
  const isFieldManual = !field?.tippingId;

  const wasteType = watch(
    `resourceRecoveryDetails.manualData.${index}.wasteType.id`,
  );

  // for manual and auto breakdown items
  useEffect(() => {
    if (isFieldManual) {
      const single = wastetypeData?.find(
        (d: IWasteType) => d?.id === wasteType,
      );
      if (single?.recyclingPercentage === 100) {
        const dataToSet = {
          mixedWaste: Array({
            wasteTypeId: wasteType,
            wasteName: single?.name,
            percentage: single?.recyclingPercentage,
          }),
          otherWaste: [
            {
              wasteTypeId: "",
              wasteName: "",
            },
          ],
        } as TWeightScaleBreakdown;

        setValue(
          `resourceRecoveryDetails.manualData.${index}.breakDown`,
          dataToSet,
        );
      } else {
        setValue(`resourceRecoveryDetails.manualData.${index}.breakDown`, {});
      }
    }
  }, [wasteType, isFieldManual, index]);

  return (
    <Table.TableRow key={field.idx + index}>
      <Table.TableCell className={cn(commonCellClassName)}>
        {index + 1}
      </Table.TableCell>
      <Table.TableCell className={cn(commonCellClassName)}>
        <FormInput
          name={`resourceRecoveryDetails.manualData.${index}.loadType.id`}
          form={form}
          render={(field) => (
            <FormSelectSingle
              field={field}
              options={
                loadtypeData?.length
                  ? loadtypeData?.map((d: ILoadType) => ({
                      label: d?.name || "",
                      value: d?.id || "",
                    }))
                  : []
              }
              isLoading={loadtypeLoading}
              placeholder='Select load-type'
              className='mt-2'
              disabled={!isFieldManual}
            />
          )}
        />
      </Table.TableCell>
      <Table.TableCell className={cn(commonCellClassName)}>
        <FormInput
          name={`resourceRecoveryDetails.manualData.${index}.loadType.quantity`}
          form={form}
          input={{
            type: "number",
            placeholder: "0",
            disabled: !isFieldManual,
          }}
        />
      </Table.TableCell>
      <Table.TableCell className={cn(commonCellClassName)}>
        <FormInput
          name={`resourceRecoveryDetails.manualData.${index}.wasteType.id`}
          form={form}
          render={(field) => (
            <FormSelectSingle
              field={field}
              className='max-w-[300px] mt-2'
              options={
                wastetypeData?.length
                  ? wastetypeData?.map((d: IWasteType) => ({
                      label: d?.name || "",
                      value: d?.id || "",
                    }))
                  : []
              }
              isLoading={wastetypeLoading}
              placeholder='Select waste-type'
              disabled={!isFieldManual}
            />
          )}
        />
      </Table.TableCell>
      <Table.TableCell className={cn(commonCellClassName)}>
        <FormInput
          name={`resourceRecoveryDetails.manualData.${index}.wasteType.weight`}
          form={form}
          key={getValues(
            `resourceRecoveryDetails.manualData.${index}.loadType.id`,
          )}
          input={{
            type: "number",
            placeholder: "0",
            step: 0.1,
            disabled: !isFieldManual,
          }}
        />
      </Table.TableCell>
      <Table.TableCell className={cn(commonCellClassName)}>
        <div className='flex flex-col gap-2 '>
          <Button
            variant={"outline"}
            className='mt-2 text-primary border-primary'
            onClick={() => {
              setSelectedBreakdownId(index);
              setBreakDownIsOpen((prev) => !prev);
            }}
            disabled={
              !watch(
                `resourceRecoveryDetails.manualData.${index}.wasteType.id`,
              ) || !isFieldManual
            }
          >
            Add Breakdown
          </Button>
        </div>
      </Table.TableCell>
      <Table.TableCell
        className={cn(commonCellClassName, " align-middle py-2")}
      >
        {field.ticketNumber ? field.ticketNumber : "-"}
      </Table.TableCell>
      <Table.TableCell className={cn(commonCellClassName)}>
        <Trash2
          size={20}
          color='red'
          className='cursor-pointer '
          onClick={() => {
            handleItemsDelete(index, field.id, field.tippingId);
          }}
        />
      </Table.TableCell>
    </Table.TableRow>
  );
};
